import { Component, OnInit, ViewChild } from '@angular/core';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { HttpService } from 'src/app/services/http.service';
import { environment } from 'src/environments/environment';
import {
  VisitorIdentityFilter,
  VisitorSessionEvent,
  VisitorSessionListFilters,
  VisitorSessionListItem,
} from 'src/app/models/visitor-session';
import {
  BackendVisitorSessionDetailResponse,
  mapVisitorSessionDetailResponse,
} from 'src/app/utils/visitor-session-api-mapper.util';
import { PageEntity } from 'src/app/models/types';
import {
  formatEventMetadata,
  formatEventSummary,
  formatEventTimestamp,
  formatIdentity,
  formatLanding,
  formatSourceSummary,
  safeDash,
  shortId,
  summarizeLandingJourney,
} from 'src/app/utils/visitor-session-display';
import {
  buildSessionAiPrompt,
  buildSessionExportJson,
} from 'src/app/utils/visitor-session-export.util';

const UNAUTHORIZED_MESSAGE =
  'Your account is not authorized to view platform visitor sessions.';

@Component({
  selector: 'app-visitor-sessions',
  templateUrl: './visitor-sessions.component.html',
  styleUrls: ['./visitor-sessions.component.scss'],
})
export class VisitorSessionsComponent implements OnInit {
  @ViewChild('sessionsTable') public dataTable: Table | undefined;

  data: VisitorSessionListItem[] = [];
  cols = [
    { field: 'createdAt', header: 'Created' },
    { field: 'lastActivityAt', header: 'Last Activity' },
    { field: 'sessionId', header: 'Session' },
    { field: 'visitorId', header: 'Visitor' },
    { field: 'identified', header: 'Type' },
    { field: 'userId', header: 'User' },
    { field: 'landingPath', header: 'Landing' },
    { field: 'sourceSummary', header: 'Origin / UTM' },
    { field: 'language', header: 'Language' },
    { field: 'deviceType', header: 'Device' },
    { field: 'eventCount', header: 'Events' },
  ];

  identityOptions = [
    { label: 'All', value: 'all' as VisitorIdentityFilter },
    { label: 'Identified', value: 'identified' as VisitorIdentityFilter },
    { label: 'Anonymous', value: 'anonymous' as VisitorIdentityFilter },
  ];

  filters: VisitorSessionListFilters = this.createDefaultFilters();
  draftFilters: VisitorSessionListFilters = this.createDefaultFilters();

  isLoading = false;
  pageIndex = 0;
  pageSize = 15;
  firstInPage = 0;
  totalRecords = 0;
  lastQuery = '';

  isDetailOpen = false;
  isDetailLoading = false;
  detailSession: VisitorSessionListItem | null = null;
  detailEvents: VisitorSessionEvent[] = [];
  isManualCopyOpen = false;
  manualCopyTitle = '';
  manualCopyText = '';

  readonly shortId = shortId;
  readonly formatIdentity = formatIdentity;
  readonly formatLanding = formatLanding;
  readonly formatSourceSummary = formatSourceSummary;
  readonly safeDash = safeDash;
  readonly formatEventTimestamp = formatEventTimestamp;
  readonly formatEventMetadata = formatEventMetadata;
  readonly formatEventSummary = formatEventSummary;
  readonly summarizeLandingJourney = summarizeLandingJourney;

  private readonly apiUrl = environment.apiUrl;

  constructor(
    private httpService: HttpService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {}

  loadData(event: LazyLoadEvent): void {
    this.isLoading = true;
    this.firstInPage = event.first || 0;
    this.pageSize = event.rows || this.pageSize || 15;
    this.pageIndex =
      this.pageSize > 0 ? Math.floor(this.firstInPage / this.pageSize) : 0;
    const url = this.buildListUrl(this.pageIndex, this.pageSize);
    this.lastQuery = url;
    this.search(url);
  }

  applyFilters(): void {
    this.filters = this.cloneFilters(this.draftFilters);
    this.firstInPage = 0;
    this.pageIndex = 0;
    this.loadData({ first: 0, rows: this.pageSize });
  }

  clearFilters(): void {
    this.draftFilters = this.createDefaultFilters();
    this.applyFilters();
  }

  async search(url: string): Promise<void> {
    const el = document.getElementsByClassName('page-container')[0];
    if (el) {
      el.scrollTop = 0;
    }

    try {
      const response: PageEntity<VisitorSessionListItem> =
        await this.httpService.doGet(url).toPromise();
      if (response) {
        this.data = response.content || [];
        this.totalRecords = response.totalElements || 0;
      } else {
        this.clearListState();
      }
      this.isLoading = false;
    } catch (error: any) {
      this.handleApiError(error, 'list');
      this.isLoading = false;
    }
  }

  async viewItem(item: VisitorSessionListItem): Promise<void> {
    if (!item.sessionId) {
      return;
    }

    this.isDetailOpen = true;
    this.isDetailLoading = true;
    this.detailSession = item;
    this.detailEvents = [];

    const url = `${this.apiUrl}/admin-center/session-tracking/v1/visitor-sessions/${encodeURIComponent(
      item.sessionId
    )}`;

    try {
      const response: BackendVisitorSessionDetailResponse =
        await this.httpService.doGet(url).toPromise();
      const mapped = mapVisitorSessionDetailResponse(response);
      this.detailSession = mapped.session || item;
      this.detailEvents = mapped.events;
      this.isDetailLoading = false;
    } catch (error: any) {
      this.isDetailLoading = false;
      this.handleApiError(error, 'detail');
    }
  }

  hideDetailDialog(): void {
    this.isDetailOpen = false;
    this.isDetailLoading = false;
    this.detailSession = null;
    this.detailEvents = [];
    this.isManualCopyOpen = false;
    this.manualCopyText = '';
    this.manualCopyTitle = '';
  }

  async copySessionJson(): Promise<void> {
    if (!this.detailSession) {
      return;
    }
    const text = buildSessionExportJson(this.detailSession, this.detailEvents);
    await this.copyToClipboard(text, 'Copied session JSON.');
  }

  async copyAiPrompt(): Promise<void> {
    if (!this.detailSession) {
      return;
    }
    const text = buildSessionAiPrompt(this.detailSession, this.detailEvents);
    await this.copyToClipboard(text, 'Copied AI prompt.');
  }

  closeManualCopyDialog(): void {
    this.isManualCopyOpen = false;
    this.manualCopyText = '';
    this.manualCopyTitle = '';
  }

  getEventRoute(event: VisitorSessionEvent): string {
    const nested = event.pageContext as { routePath?: string } | undefined;
    return safeDash(event.routePath || event.path || nested?.routePath);
  }

  getEventLanding(event: VisitorSessionEvent): string {
    const nested = event.pageContext as
      | { landingPath?: string; landingKey?: string; surface?: string }
      | undefined;
    const path = (event.landingPath ?? nested?.landingPath)?.trim();
    const key = (event.landingKey ?? nested?.landingKey)?.trim();
    const page = (event.page ?? nested?.surface)?.trim();
    if (path && key) {
      return `${path} (${key})`;
    }
    return safeDash(path || key || page);
  }

  getEventSource(event: VisitorSessionEvent): string {
    const source =
      event.sourceSummary ??
      (event.source as VisitorSessionListItem['sourceSummary']);
    if (!source) {
      return '-';
    }
    if (typeof source === 'string') {
      return safeDash(source);
    }
    return formatSourceSummary({ sourceSummary: source });
  }

  getEventMetadata(event: VisitorSessionEvent): string {
    const metadata =
      event.metadata ??
      event.payload ??
      event.actionDetails ??
      event.action;
    return formatEventMetadata(metadata);
  }

  private buildListUrl(page: number, size: number): string {
    const params = new URLSearchParams();
    params.set('from', this.startOfDay(this.filters.from).toISOString());
    params.set('to', this.endOfDay(this.filters.to).toISOString());
    params.set('page', String(page));
    params.set('size', String(size));

    const landingPath = this.filters.landingPath.trim();
    const landingKey = this.filters.landingKey.trim();
    const visitorId = this.filters.visitorId.trim();

    if (landingPath) {
      params.set('landingPath', landingPath);
    }
    if (landingKey) {
      params.set('landingKey', landingKey);
    }
    if (visitorId) {
      params.set('visitorId', visitorId);
    }

    if (this.filters.identity === 'identified') {
      params.set('identifiedOnly', 'true');
    } else if (this.filters.identity === 'anonymous') {
      params.set('anonymousOnly', 'true');
    }

    return `${this.apiUrl}/admin-center/session-tracking/v1/visitor-sessions?${params.toString()}`;
  }

  private createDefaultFilters(): VisitorSessionListFilters {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 7);

    return {
      from,
      to,
      landingKey: '',
      landingPath: '',
      visitorId: '',
      identity: 'all',
    };
  }

  private cloneFilters(
    filters: VisitorSessionListFilters
  ): VisitorSessionListFilters {
    return {
      from: new Date(filters.from),
      to: new Date(filters.to),
      landingKey: filters.landingKey,
      landingPath: filters.landingPath,
      visitorId: filters.visitorId,
      identity: filters.identity,
    };
  }

  private startOfDay(date: Date): Date {
    const value = new Date(date);
    value.setHours(0, 0, 0, 0);
    return value;
  }

  private endOfDay(date: Date): Date {
    const value = new Date(date);
    value.setHours(23, 59, 59, 999);
    return value;
  }

  private clearListState(): void {
    this.data = [];
    this.totalRecords = 0;
    this.firstInPage = 0;
    this.pageIndex = 0;
  }

  private handleApiError(error: any, context: 'list' | 'detail'): void {
    if (this.isUnauthorized(error)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: UNAUTHORIZED_MESSAGE,
      });
      if (context === 'list') {
        this.clearListState();
      }
      if (context === 'detail') {
        this.hideDetailDialog();
      }
      return;
    }

    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: this.getErrorMessage(error),
    });

    if (context === 'list') {
      this.clearListState();
    }
  }

  private isUnauthorized(error: any): boolean {
    const status = error?.status;
    const backendMessage = this.getBackendMessage(error).toLowerCase();
    return (
      status === 401 ||
      status === 403 ||
      (status === 400 && backendMessage.includes('unauthorized'))
    );
  }

  private getBackendMessage(error: any): string {
    if (typeof error?.error === 'string') {
      return error.error;
    }
    return (
      error?.error?.message ||
      error?.error?.detail ||
      error?.message ||
      ''
    ).toString();
  }

  private getErrorMessage(error: any): string {
    const backendMessage = this.getBackendMessage(error);
    return backendMessage || 'An unexpected error occurred.';
  }

  private async copyToClipboard(
    text: string,
    successMessage: string
  ): Promise<void> {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: successMessage,
        });
        return;
      }
      throw new Error('Clipboard API unavailable');
    } catch {
      this.manualCopyTitle = successMessage.replace(/^Copied /i, 'Copy ');
      this.manualCopyText = text;
      this.isManualCopyOpen = true;
      this.messageService.add({
        severity: 'warn',
        summary: 'Copy manually',
        detail: 'Could not copy automatically. Please copy manually.',
      });
    }
  }
}
